import { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2, MessageCircle, RefreshCw, Search, Diamond, Sparkles, ExternalLink } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "new", label: "Новая", color: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "in_progress", label: "В работе", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "won", label: "Закрыта успешно", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "lost", label: "Потеряна", color: "bg-rose-100 text-rose-700 border-rose-200" },
  { value: "closed", label: "Закрыта", color: "bg-gray-100 text-gray-700 border-gray-200" },
];

const statusMeta = (s) => STATUS_OPTIONS.find(x => x.value === s) || STATUS_OPTIONS[0];

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ts; }
}

function waLink(contact) {
  if (!contact) return null;
  const digits = (contact.match(/\d+/g) || []).join("");
  return digits.length >= 9 ? `https://wa.me/${digits}` : null;
}

export function LeadsTab({ password }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ wedding: 0, bouquet: 0 });
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/leads/all?password=${encodeURIComponent(password)}&limit=500`);
      const d = await r.json();
      setLeads(d.leads || []);
      setCounts({ wedding: d.wedding_count || 0, bouquet: d.bouquet_count || 0 });
    } catch { /* keep prior */ }
    setLoading(false);
  }, [password]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter(l => {
      if (typeFilter !== "all" && l.type !== typeFilter) return false;
      if (statusFilter !== "all" && (l.status || "new") !== statusFilter) return false;
      if (q && !(`${l.name} ${l.contact} ${l.city} ${l.message}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [leads, typeFilter, statusFilter, search]);

  const updateLead = async (lead, patch) => {
    const r = await fetch(`/api/admin/leads/${lead.type}/${lead.id}?password=${encodeURIComponent(password)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (r.ok) {
      setLeads(prev => prev.map(x => x.id === lead.id && x.type === lead.type ? { ...x, ...patch } : x));
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Удалить заявку от ${lead.name}? Это действие нельзя отменить.`)) return;
    const r = await fetch(`/api/admin/leads/${lead.type}/${lead.id}?password=${encodeURIComponent(password)}`, { method: "DELETE" });
    if (r.ok) setLeads(prev => prev.filter(x => !(x.id === lead.id && x.type === lead.type)));
  };

  return (
    <div className="space-y-4" data-testid="leads-tab">
      {/* Header bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            <button onClick={() => setTypeFilter("all")} className={`px-3 py-1.5 text-sm rounded-full font-medium ${typeFilter === "all" ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`} data-testid="leads-filter-all">
              Все ({leads.length})
            </button>
            <button onClick={() => setTypeFilter("wedding")} className={`px-3 py-1.5 text-sm rounded-full font-medium flex items-center gap-1.5 ${typeFilter === "wedding" ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`} data-testid="leads-filter-wedding">
              <Diamond className="w-3.5 h-3.5" /> Свадьбы ({counts.wedding})
            </button>
            <button onClick={() => setTypeFilter("bouquet")} className={`px-3 py-1.5 text-sm rounded-full font-medium flex items-center gap-1.5 ${typeFilter === "bouquet" ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`} data-testid="leads-filter-bouquet">
              <Sparkles className="w-3.5 h-3.5" /> Букеты ({counts.bouquet})
            </button>
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white" data-testid="leads-filter-status">
            <option value="all">Все статусы</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 lg:max-w-md">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск по имени, контакту, городу…"
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg" data-testid="leads-search" />
          </div>
          <button onClick={load} disabled={loading} className="p-2 text-gray-500 hover:text-pink-500 hover:bg-pink-50 rounded-lg" data-testid="leads-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Leads list */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-gray-500" data-testid="leads-empty">
          {loading ? "Загружаем заявки…" : "Заявок пока нет."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(lead => {
            const isOpen = expanded === `${lead.type}-${lead.id}`;
            const sm = statusMeta(lead.status || "new");
            const wa = waLink(lead.contact);
            return (
              <div key={`${lead.type}-${lead.id}`} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`lead-${lead.id}`}>
                <button onClick={() => setExpanded(isOpen ? null : `${lead.type}-${lead.id}`)} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left">
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide border ${sm.color}`}>
                    {sm.label}
                  </span>
                  <span className="text-xs text-gray-400 font-mono shrink-0">
                    {lead.type === "wedding" ? <Diamond className="w-3.5 h-3.5 inline text-pink-500" /> : <Sparkles className="w-3.5 h-3.5 inline text-amber-500" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm truncate">{lead.name}</span>
                      <span className="text-xs text-gray-500 font-mono">{lead.contact}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>📍 {lead.city || "—"}</span>
                      {lead.budget && <span>💶 {lead.budget}</span>}
                      {lead.occasion && <span>🎁 {lead.occasion}</span>}
                      {lead.wedding_date && <span>📅 {lead.wedding_date}</span>}
                      {lead.guests && <span>👥 {lead.guests}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0 hidden sm:block">{fmtTs(lead.ts)}</span>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3" data-testid={`lead-detail-${lead.id}`}>
                    {lead.message && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-semibold">Сообщение</div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.message}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                      {lead.source_page && <div><span className="text-gray-500">Откуда:</span> <code className="text-gray-700">{lead.source_page}</code></div>}
                      <div><span className="text-gray-500">Язык:</span> <code className="text-gray-700 uppercase">{lead.lang || "ru"}</code></div>
                      <div><span className="text-gray-500">Время:</span> <span className="text-gray-700">{fmtTs(lead.ts)}</span></div>
                      <div><span className="text-gray-500">ID:</span> <code className="text-gray-700 text-[10px]">{lead.id?.slice(0, 8) || "—"}</code></div>
                    </div>

                    {/* Note */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 font-semibold">Заметка менеджера</div>
                      <textarea
                        rows={2}
                        defaultValue={lead.note || ""}
                        onChange={e => setNoteDraft(prev => ({ ...prev, [lead.id]: e.target.value }))}
                        onBlur={e => {
                          const v = e.target.value;
                          if ((noteDraft[lead.id] ?? lead.note ?? "") !== (lead.note ?? "") || v !== (lead.note ?? "")) {
                            updateLead(lead, { note: v });
                          }
                        }}
                        placeholder="Договорились созвон 15:00, отправить макет…"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
                        data-testid={`lead-note-${lead.id}`}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <select
                        value={lead.status || "new"}
                        onChange={e => updateLead(lead, { status: e.target.value })}
                        className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white font-medium"
                        data-testid={`lead-status-${lead.id}`}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>→ {s.label}</option>)}
                      </select>
                      {wa && (
                        <a href={wa} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition">
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      )}
                      {lead.source_page && (
                        <a href={lead.source_page} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition">
                          <ExternalLink className="w-3.5 h-3.5" /> Страница
                        </a>
                      )}
                      <button onClick={() => deleteLead(lead)}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        data-testid={`lead-delete-${lead.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Удалить
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
